/* ═══════════ WhatsAppTemplate body rendering ═══════════
   Admin-editable templates (`STG_TREXA.WhatsAppTemplate`) carry single-brace `{token}`
   placeholders, listed per template in `VarsJson` and validated against `WA_VAR_PALETTE`
   when an admin saves one. Every send path has to fill them the same way, so the regex
   lives here rather than being re-typed at each call site. */
import type { WhatsAppTemplateSdk } from '@libs/graphql/sdk/whats-app-template/whats-app-template.sdk';
import type { WhatsAppTemplatePoco } from '@libs/graphql/models';

/**
 * The admin-editable row behind a template code — `null` when the table isn't deployed, the seed
 * hasn't been run, or the read fails.
 *
 * Every transactional send that honours /admin/whatsapp does the same two steps: read the row by
 * code, then use it only when `waTemplateUsable` says so. Both halves live here so each new send
 * path doesn't re-type the pair and quietly drop the `Enabled` check.
 *
 * Note what "disabled" means today, because it is not obvious: an admin switching a template off
 * changes the WORDING back to the hard-coded builder — it does not silence the message. That is
 * the behaviour `notifyClawbackVendors` has always had and this keeps it, on the reasoning that a
 * payment reminder or a debt notice is part of the flow rather than a marketing opt-in. If the
 * toggle should instead stop the send, it has to change in both places at once.
 */
export async function loadWaTemplate(
  sdk: WhatsAppTemplateSdk, code: string,
): Promise<WhatsAppTemplatePoco | null> {
  try {
    const res = await sdk.list(
      { code: { eq: code } }, [{ column: 'id', desc: false }],
      ['id', 'guid', 'code', 'audience', 'label', 'body', 'enabled'], 1, 1,
    );
    return ((res.data as WhatsAppTemplatePoco[]) ?? [])[0] ?? null;
  } catch {
    return null;   // undeployed table / read failure — the hard-coded fallback still sends
  }
}

/** Is this row usable as the body of a send? (Present, enabled, and not blank.) */
export function waTemplateUsable(tpl: WhatsAppTemplatePoco | null): boolean {
  return !!tpl?.enabled && !!(tpl.body ?? '').trim();
}

/**
 * Fill a WhatsAppTemplate body's single-brace `{token}`s.
 *
 * An unknown token is left visible as `{token}` rather than replaced with an empty string: a
 * broken template then shows up in the delivered message (and in the admin History tab), instead
 * of silently sending a sentence with a hole in it.
 */
export function renderWaTemplate(body: string, vars: Record<string, string>): string {
  return (body ?? '').replace(/\{(\w+)\}/g, (_m, k: string) => vars[k] ?? `{${k}}`);
}

/**
 * Body + History label for one send: the admin template behind `code` when it is usable, the
 * hard-coded wording otherwise. The three steps every transactional send repeats, in one place.
 */
export async function waTemplateMessage(
  sdk: WhatsAppTemplateSdk, code: string, vars: Record<string, string>,
  fallbackBody: string, fallbackLabel: string,
): Promise<{ body: string; label: string }> {
  const tpl = await loadWaTemplate(sdk, code);
  return {
    body: waTemplateUsable(tpl) ? renderWaTemplate(tpl?.body ?? '', vars) : fallbackBody,
    label: tpl?.label || fallbackLabel,
  };
}
